import { useState, useEffect } from 'react';
import { useAuth } from '../../../auth';
import type { Profile, ApplicationStatus } from '@modelcast/types';
import { ApplicationReviewModal } from './ApplicationReviewModal';

export function ApplicationsPage() {
  const { getToken } = useAuth();
  const [applications, setApplications] = useState<Profile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [filter, setFilter] = useState<ApplicationStatus>('pending');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchApplications();
  }, [filter]);

  async function fetchApplications() {
    setIsLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/applications?status=${filter}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setApplications(data.profiles);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleReview(profileId: string, status: 'approved' | 'rejected') {
    let note: string | undefined;

    if (status === 'rejected') {
      const rejectionNote = window.prompt('Audit note for rejecting this application (required):');
      if (rejectionNote === null) return;
      if (!rejectionNote.trim()) {
        alert('An audit note is required to reject an application.');
        return;
      }
      note = rejectionNote.trim();
    }

    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/applications/${profileId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status, ...(note ? { note } : {}) }),
      });

      if (response.ok) {
        setSelectedProfile(null);
        fetchApplications();
      }
    } catch (error) {
      console.error('Error updating application:', error);
    }
  }

  const filterOptions: { value: ApplicationStatus; label: string }[] = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-medium text-text">Model Applications</h1>
          <p className="text-sm text-text-dim mt-1">Review and manage model registration requests</p>
        </div>
        <div className="flex gap-1 bg-panel2 rounded-lg p-1">
          {filterOptions.map(option => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                filter === option.value
                  ? 'bg-bg text-text font-medium shadow-sm'
                  : 'text-text-dim hover:text-text'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-text-dim">Loading...</div>
        </div>
      ) : applications.length === 0 ? (
        <div className="bg-panel rounded-xl border border-line p-8 text-center">
          <div className="text-text-dim">No {filter} applications</div>
        </div>
      ) : (
        <div className="bg-panel rounded-xl border border-line overflow-hidden">
          <table className="w-full">
            <thead className="bg-panel2 border-b border-line">
              <tr>
                <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Name</th>
                <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Location</th>
                <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Applied</th>
                <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Photos</th>
                <th className="px-4 py-3 text-right text-[10px] font-mono text-text-faint uppercaser">Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.map(profile => (
                <tr key={profile.id} className="border-b border-line last:border-0 hover:bg-panel2/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {profile.photos[0] ? (
                        <img
                          src={profile.photos[0].url}
                          alt=""
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-panel2 flex items-center justify-center">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-text-faint">
                            <circle cx="12" cy="8" r="4" />
                            <path d="M20 21c0-4.418-3.582-8-8-8s-8 3.582-8 8" />
                          </svg>
                        </div>
                      )}
                      <div>
                        <div className="text-text font-medium">{profile.displayName}</div>
                        {profile.instagram && (
                          <div className="text-xs text-text-dim">@{profile.instagram}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-dim">
                    {profile.country || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-dim">
                    {new Date(profile.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-dim">
                    {profile.photos.length}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setSelectedProfile(profile)}
                      className="btn btn-sm"
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedProfile && (
        <ApplicationReviewModal
          profile={selectedProfile}
          onClose={() => setSelectedProfile(null)}
          onApprove={() => handleReview(selectedProfile.id, 'approved')}
          onReject={() => handleReview(selectedProfile.id, 'rejected')}
        />
      )}
    </div>
  );
}
